import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon } from '../Icon';
import styles from './PaginationButton.module.css';

type TrackingValue = string | number | boolean;

export interface PaginationButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  children?: ReactNode;
  isActive?: boolean;
  direction?: 'left' | 'right';
  trackEvent?: string;
  trackData?: Record<string, TrackingValue | null | undefined>;
}

export function PaginationButton({
  children,
  isActive = false,
  direction,
  disabled,
  trackEvent,
  trackData,
  className,
  ...props
}: PaginationButtonProps) {
  const classNames = [
    styles.button,
    isActive ? styles.active : '',
    disabled ? styles.disabled : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconName = direction === 'left' ? 'chevron-left' : direction === 'right' ? 'chevron-right' : undefined;

  const trackingAttributes: Record<string, string> = {};
  if (trackEvent) {
    trackingAttributes['data-track'] = trackEvent;

    if (trackData) {
      for (const [key, value] of Object.entries(trackData)) {
        if (value == null) continue;
        const attrKey = `data-${key.replace(/[A-Z]/g, m => `-${m.toLowerCase()}`)}`;
        trackingAttributes[attrKey] = String(value);
      }
    }
  }

  return (
    <button
      className={classNames}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Página anterior' : direction === 'right' ? 'Página siguiente' : `Página ${children}`}
      aria-current={isActive ? 'page' : undefined}
      {...trackingAttributes}
      {...props}
    >
      {iconName ? (
        <Icon name={iconName} size="sm" color={disabled ? 'disabled' : isActive ? 'inverse' : 'primary'} />
      ) : (
        children
      )}
    </button>
  );
}

PaginationButton.displayName = 'PaginationButton';
